<script setup lang="ts">
import { computed } from "vue";

const props = defineProps<{
    name: string;
    size: number;
    mimeType: string;
    status: "uploading" | "done" | "error";
    // Доля загруженного [0..1] при status === "uploading".
    progress?: number;
    removable?: boolean;
}>();

const emit = defineEmits<{ remove: [] }>();

function formatSize(bytes: number): string {
    if (bytes < 1024) {
        return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(0)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Короткая метка типа для иконки: расширение имени, иначе подтип mime.
const ext = computed(() => {
    const dot = props.name.lastIndexOf(".");
    if (dot > 0 && dot < props.name.length - 1) {
        return props.name
            .slice(dot + 1)
            .toUpperCase()
            .slice(0, 4);
    }
    const sub = props.mimeType.split("/")[1];
    return (sub ?? "FILE").toUpperCase().slice(0, 4);
});

const kind = computed(() => {
    if (props.mimeType.startsWith("image/")) {
        return "IMAGE";
    }
    if (props.mimeType.startsWith("video/")) {
        return "VIDEO";
    }
    if (props.mimeType.startsWith("audio/")) {
        return "AUDIO";
    }
    return ext.value;
});

// Размер во время загрузки: «(залито / всего)».
const uploadedLabel = computed(() =>
    formatSize(Math.round((props.progress ?? 0) * props.size))
);

const pct = computed(() =>
    Math.max(0, Math.min(100, Math.round((props.progress ?? 0) * 100)))
);
</script>

<template>
    <div
        class="mc-file-chip"
        :class="{ 'mc-file-chip--error': status === 'error' }"
    >
        <!-- Полоса прогресса поверх карточки при загрузке -->
        <div
            v-if="status === 'uploading'"
            class="mc-file-chip__bar"
            :style="{ width: pct + '%' }"
        />

        <div class="mc-file-chip__icon">
            <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
                stroke-linejoin="round"
            >
                <path d="M14 3v5h5" />
                <path
                    d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"
                />
            </svg>
            <span class="mc-file-chip__ext">{{ ext }}</span>
        </div>

        <div class="mc-file-chip__meta">
            <span class="mc-file-chip__name" :title="name">{{ name }}</span>
            <span class="mc-file-chip__sub">
                <template v-if="status === 'error'">ОШИБКА ЗАГРУЗКИ</template>
                <template v-else-if="status === 'uploading'">
                    {{ kind }} · {{ formatSize(size) }}
                    <span class="mc-file-chip__uploaded"
                        >({{ uploadedLabel }})</span
                    >
                </template>
                <template v-else>{{ kind }} · {{ formatSize(size) }}</template>
            </span>
        </div>

        <!-- Во время загрузки × работает как отмена. -->
        <button
            v-if="removable !== false"
            class="mc-file-chip__remove"
            type="button"
            :aria-label="status === 'uploading' ? 'Отменить' : 'Удалить'"
            @click="emit('remove')"
        >
            <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
            >
                <path d="M2 2l8 8M10 2l-8 8" />
            </svg>
        </button>
    </div>
</template>

<style scoped>
.mc-file-chip {
    position: relative;
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 180px;
    max-width: 260px;
    padding: 8px 10px;
    background: var(--mc-bg-input);
    border: 1px solid var(--mc-line);
    border-radius: 10px;
    overflow: hidden;
}

.mc-file-chip--error {
    border-color: var(--mc-danger);
}

.mc-file-chip__bar {
    position: absolute;
    top: 0;
    left: 0;
    height: 2px;
    background: var(--mc-acid);
    box-shadow: 0 0 6px var(--mc-acid-glow);
    transition: width 0.15s ease;
}

.mc-file-chip__icon {
    position: relative;
    flex: 0 0 auto;
    width: 38px;
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    background: color-mix(in srgb, var(--mc-acid) 12%, transparent);
    color: var(--mc-acid);
}

.mc-file-chip__ext {
    position: absolute;
    bottom: 3px;
    left: 0;
    right: 0;
    text-align: center;
    font-family: var(--mc-mono);
    font-size: 6px;
    letter-spacing: 0.02em;
    color: var(--mc-acid);
    pointer-events: none;
}

.mc-file-chip__meta {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.mc-file-chip__name {
    font-size: 13px;
    font-weight: 600;
    color: var(--mc-fg);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.mc-file-chip__sub {
    font-family: var(--mc-mono);
    font-size: 10px;
    letter-spacing: 0.02em;
    color: var(--mc-fg-mute);
    text-transform: uppercase;
    white-space: nowrap;
}

.mc-file-chip--error .mc-file-chip__sub {
    color: var(--mc-danger);
}

.mc-file-chip__uploaded {
    color: var(--mc-acid);
}

.mc-file-chip__remove {
    flex: 0 0 auto;
    margin-left: auto;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    background: none;
    border: none;
    border-radius: 6px;
    color: var(--mc-fg-dim);
    cursor: pointer;
    transition:
        color 0.12s ease,
        background 0.12s ease;
}

.mc-file-chip__remove:hover {
    color: var(--mc-fg);
    background: var(--mc-bg-sel);
}
</style>
